<script setup>
import { ref, computed, onMounted } from "vue";
import { helpers, required, email } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import Autocomplete from "@/components/company/forms/elements/Autocomplete.vue";
import workforce from "@/config/lists/workforce";

import {
  BaseKit,
  Bold,
  Color,
  Fullscreen,
  Heading,
  Highlight,
  History,
  Image,
  Italic,
  Link,
  Strike,
  Table,
  Underline,
  Video,
  VuetifyTiptap,
} from "vuetify-pro-tiptap";
import "vuetify-pro-tiptap/style.css";
import { useCompanyStore, useListStore, useUserStore } from "@/stores";

const extensions = [
  BaseKit.configure({
    placeholder: {
      placeholder: "Enter some text...",
    },
  }),
  Bold,
  Italic,
  Underline,
  Strike,
  Color,
  Highlight,
  Heading,
  Link,
  Image,
  Video,
  Table,
  Fullscreen,
  History,
];

const userStore = useUserStore();
const listStore = useListStore();
const companyStore = useCompanyStore();

const user = userStore.user;

const branches = computed(() => listStore.branches);
const companiesSectors = computed(() => listStore.companiesSectors);

const communicationResponsibles = computed(
  () => listStore.communicationResponsibles
);

const submitForm = async () => {
  const isValid = await v$.value.$validate();
  form.value.createdUserId = user.id;

  const data = form.value;

  if (isValid) {
    await companyStore.createCompany(data);
  }
};

const rules = {
  name: {
    required: helpers.withMessage("Company name is required", required),
  },
  email: {
    email: helpers.withMessage("Invalid email format", email),
  },
  contactEmail: {
    email: helpers.withMessage("Invalid email format", email),
  },
  contactName: {
    required: helpers.withMessage("Contact name is required", required),
  },
};

const form = ref({
  name: null,
  logo: null,
  email: null,
  website: null,
  location: null,
  workforce: null,
  contactName: null,
  contactEmail: null,
  contactTelephone: null,
  talentPlatformProfile: null,
  branch: null,
  notes: null,
  jobPositions: null,
  companySector: null,
});

const v$ = useVuelidate(rules, form);

const isRequired = (field) => {
  return "required" in rules[field];
};

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

onMounted(async () => {
  await listStore.fetchBranches();
  await listStore.fetchCommunicationResponsibles(true);
  await listStore.fetchCompaniesSectors();
});
</script>

<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="6">
        <h1 class="text-company">Add Company</h1>
      </v-col>
      <v-col cols="6" class="d-flex justify-end">
        <v-btn color="company" size="large" @click="submitForm">
          <v-icon icon="mdi-plus" /> Save new entry
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-form class="custom-form mt-5 d-flex flex-column">
          <v-file-input
            v-model="form.image"
            accept="image/*"
            variant="outlined"
            prepend-icon=""
            prepend-inner-icon="mdi-camera"
            hide-details="auto"
          >
            <template #label> Upload Logo </template>
          </v-file-input>

          <v-text-field
            v-model="form.name"
            name="name"
            :error-messages="getErrorMessages('name').value"
            hide-details="auto"
            @blur="v$.name.$touch()"
          >
            <template #label>
              Company Name
              <span v-if="isRequired('name')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-text-field>

          <v-text-field
            v-model="form.email"
            name="email"
            hide-details="auto"
            :error-messages="getErrorMessages('email').value"
            @blur="v$.email.$touch()"
          >
            <template #label> Email </template>
          </v-text-field>

          <v-text-field
            v-model="form.website"
            name="website"
            hide-details="auto"
            hint="This must be an external URL such as https://example.com"
          >
            <template #label> Website </template>
          </v-text-field>

          <v-text-field
            v-model="form.telephone"
            name="telephone"
            hide-details="auto"
          >
            <template #label> Telephone </template>
          </v-text-field>

          <v-text-field
            v-model="form.location"
            name="location"
            hide-details="auto"
          >
            <template #label> Location </template>
          </v-text-field>

          <v-select
            v-model="form.workforce"
            :items="workforce"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label> Workforce </template>
          </v-select>

          <v-text-field
            v-model="form.contactName"
            name="contactName"
            :error-messages="getErrorMessages('contactName').value"
            hide-details="auto"
            @blur="v$.contactName.$touch()"
          >
            <template #label>
              Contact Name
              <span v-if="isRequired('contactName')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-text-field>

          <v-text-field
            v-model="form.contactEmail"
            name="contactEmail"
            hide-details="auto"
            :error-messages="getErrorMessages('contactEmail').value"
            @blur="v$.contactEmail.$touch()"
          >
            <template #label> Contact Email </template>
          </v-text-field>

          <v-text-field
            v-model="form.contactTelephone"
            name="contactTelephone"
            hide-details="auto"
          >
            <template #label> Contact Telephone </template>
          </v-text-field>

          <Autocomplete
            v-model="form.branch"
            label="Odyssea Branch"
            :items="branches"
            :multiple="false"
            :chips="false"
          />

          <Autocomplete
            v-model="form.responsible"
            label="Communication responsible"
            :items="communicationResponsibles"
            :multiple="false"
            :chips="false"
          />

          <v-text-field
            v-model="form.talentPlatformProfile"
            name="talentPlatformProfile"
            hide-details="auto"
            hint="This must be an external URL such as https://example.com"
          >
            <template #label> Talent Platform Profile </template>
          </v-text-field>

          <Autocomplete
            v-model="form.companySector"
            label="Company Sector"
            :items="companiesSectors"
            :multiple="false"
            :chips="false"
          />

          <VuetifyTiptap
            v-model="form.notes"
            label="Notes & History"
            rounded
            :min-height="200"
            :extensions="extensions"
          />
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-company));
}
.flex-50 {
  flex: 0 0 50%;
}
</style>
